import _ from 'underscore';
import $ from 'jquery';
import Backbone from 'backbone';
import { SimpleForm } from '@bingads-webui/simple-form';
import { focusLockInstanceDecorator } from '@bingads-webui/focus-lock-decorator';
import { getTabbableNodes } from '@bingads-webui-react/focus-lock';
import { getPosition } from './get-position';

/**
 * Popup editor view based on simple form & schema
 * <br> User only need to specify schema & fields can edit, no need to prepare content in editor
 *
 * <br>It enabled functions:
 *   prompt: open a popup editor,  return promise which will be resolved after save & cancel
 *   dismiss: close the editor
 *
 * <br>Triggered event selectedLanguages:changed when user selection changes
 */

export class PopupEditor extends Backbone.View {
  /**
   * Initilize view settings
   *
   * @param {Object} [position] - the position where inline edit was triggered
   * @param {JsonSchema} [schema] - entity define
   * @param {String[]} [fields] - the field names in schema that allowed inline editing
   * @param {Object} [model] - undefined
   * @param {Object} [saveModel] - custom function to handle event when save clicked, optional
   * @param {CustomValidators} [customValidators] - {}
   * @param {SideBarOption[]} [sidebars] - []
   * @param {String|function} [template] - horizontal
   * @param {boolean} [showButtons] - true
   * @param {boolean} [hasLabels] - true
   * @param {String} [customLabel] - custom label
   * @param {String} [customHint] - custom hint
   * @param {String} [inputSuffix] - input suffix
   * @param {function} [labelTemplate] - src/template/simple-form/control/label.pug
   * @param {S$I18n} [s$i18n] - get i18n string of schema
   * @param {I18n} [i18n] - get i18n string in component
   *
   * @returns {undefined} - initialize view
   */
  initialize({
    position, // see ```function getPosition(pos, popupEditor)``` for avaliable option

    // TODO: [zhbliu] options for form
    // ES7 Rest/Spread properties will help here when our Babel support that
    schema,
    fields,
    s$i18n,
    i18n,
    model,
    saveModel,
    customValidators,
    sidebars,
    template = 'horizontal',
    hasButtons = true,
    hasLabels = true,
    customLabel = null,
    customHint = null,
    inputSuffix = null,
    labelTemplate,
    customClass,
    lockRegionAriaLabel = '',
  }) {
    this.position = position;

    this.editorForm = (new SimpleForm({
      schema,
      fields,
      s$i18n,
      i18n,
      customValidators,
      sidebars,
      template,
      hasButtons,
      hasLabels,
      customLabel,
      customHint,
      inputSuffix,
      labelTemplate,
      model,
    })).render();

    this.editorForm.on('save', (updatedModel) => {
      if (_.isFunction(saveModel)) {
        saveModel(updatedModel, this);
      } else {
        this.trigger('save', updatedModel);
      }
    });
    this.editorForm.on('cancel', () => this.trigger('cancel'));

    const FocusLockSimpleForm = focusLockInstanceDecorator(this.editorForm, {
      onExit: () => this.trigger('cancel'),
      lockRegionAriaLabel,
      focusFirstItemOnMount: true,
      skipFocusOnMount: true,
    }, {
      managed: true,
    });

    this.focusLockEditorForm = new FocusLockSimpleForm();

    this.$el.attr('role', 'dialog');
    this.customClass = customClass;
  }

  render() {
    this.$el.addClass('popup-editor');
    if (this.customClass) {
      this.$el.addClass(this.customClass);
    }
    this.$el.append(this.focusLockEditorForm.render().el);
    this.setPosition(this.position);

    return this;
  }

  setPosition(pos) {
    const position = getPosition(pos, this);

    this.$el.css({
      position: 'absolute',
      visibility: 'hidden',
      left: 0,
      top: 0,
    });

    // Relocate the popup editor when its layout is ready
    window.setTimeout(() => {
      const $offsetParent = this.$el.offsetParent();
      const parentWidth = $offsetParent.width();
      const parentHeight = $offsetParent.height();
      const editorWidth = this.$el.outerWidth();
      const editorHeight = this.$el.outerHeight();

      let left = _.result(position, 'left', (parentWidth - editorWidth) / 2);

      if (left + editorWidth > parentWidth) {
        left = _.result(position, 'right', parentWidth) - editorWidth;
      }

      let top = _.result(position, 'top', (parentHeight - editorHeight) / 2);

      if (top + editorHeight > parentHeight) {
        top = _.result(position, 'bottom', parentHeight) - editorHeight;
      }

      this.$el.css({ visibility: 'visible', left, top });

      // popup editor focus should be done after the element is visible
      if (this.editorForm.el) {
        // we need to filter those unvisible element which due to parent's display property
        const tabbableNode = _.filter(getTabbableNodes(this.editorForm.el), item => $(item).is(':visible'));

        if (tabbableNode && tabbableNode.length > 0) {
          tabbableNode[0].focus({
            preventScroll: true,
          });
        }

        // add data-focused flag to indicate the focus operation is done
        this.$el.attr('data-focused', '');
      }
    }, 0);
  }

  setErrors(errors) {
    this.editorForm.setErrors(errors);
  }

  remove() {
    this.focusLockEditorForm.remove();
    this.editorForm.remove();
    super.remove();
  }
}
